/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'

import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {
    Button,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CONFIRMATION_DIALOG_DEFAULT_CONFIG} from '@salesforce/retail-react-app/app/pages/account/constant'
import {colors} from '../../constants'

const ConfirmationModal = ({
    dialogTitle = CONFIRMATION_DIALOG_DEFAULT_CONFIG.dialogTitle,
    confirmationMessage = CONFIRMATION_DIALOG_DEFAULT_CONFIG.confirmationMessage,
    primaryActionLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.primaryActionLabel,
    alternateActionLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.alternateActionLabel,
    onPrimaryAction = noop,
    onAlternateAction = noop,
    ...props
}) => {
    const {formatMessage} = useIntl()

    const handleConfirmClick = () => {
        onPrimaryAction()
        props.onClose()
    }

    const handleAlternateActionClick = () => {
        onAlternateAction()
        props.onClose()
    }
    return (
        <AlertDialog
            isOpen={props.isOpen}
            isCentered
            onClose={handleAlternateActionClick}
            {...props}
        >
            <AlertDialogOverlay />
            <AlertDialogContent
                fontFamily="Raleway"
                fontWeight="300"
                containerProps={{
                    zIndex: 1999
                }}
            >
                <AlertDialogHeader fontStyle="400">
                    {formatMessage(dialogTitle)}
                </AlertDialogHeader>
                <AlertDialogBody fontFamily="Raleway" fontWeight="300">
                    <Text>{formatMessage(confirmationMessage)}</Text>
                </AlertDialogBody>

                <AlertDialogFooter>
                    <Button
                        variant="ghost"
                        mr={3}
                        onClick={handleAlternateActionClick}
                        isLoading={props.isLoading}
                        fontFamily="Raleway"
                        fontWeight="300"
                        color={colors.black}
                        _hover="none"
                        _focus={{
                            background: colors.white,
                            color: colors.black,
                            backgroundColor: colors.white
                        }}
                    >
                        {formatMessage(alternateActionLabel)}
                    </Button>
                    <Button
                        variant="solid"
                        onClick={handleConfirmClick}
                        color={colors.white}
                        backgroundColor={colors.black}
                        fontFamily="Raleway"
                        fontWeight="300"
                        background={colors.black}
                        isLoading={props.isLoading}
                        _hover="none"
                        _focus={{
                            background: colors.black,
                            color: colors.white,
                            backgroundColor: colors.black
                        }}
                    >
                        {formatMessage(primaryActionLabel)}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

ConfirmationModal.propTypes = {
    /**
     * Prop to check if modal is open
     */
    isOpen: PropTypes.bool.isRequired,
    /**
     * Callback invoked to open the modal
     */
    onOpen: PropTypes.func.isRequired,
    /**
     * Callback invoked to close the modal
     */
    onClose: PropTypes.func.isRequired,
    /**
     * Text to be displayed as modal header
     */
    dialogTitle: PropTypes.object,
    /**
     * Text to display in confirmation modal prompting user to pick an action
     */
    confirmationMessage: PropTypes.object,
    /**
     * Button Label for primary action in confirmation modal
     */
    primaryActionLabel: PropTypes.object,
    /**
     * Button Label for alternate or secondary action in confirmation modal
     */
    alternateActionLabel: PropTypes.object,
    /**
     * Action to execute if user selects primary action
     */
    onPrimaryAction: PropTypes.func,
    /**
     * Action to execute if user selects alternate or secondary action
     */
    onAlternateAction: PropTypes.func,
    isLoading: PropTypes.func
}

export default ConfirmationModal
